import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Brain, Edit3, Sparkles, ArrowRight } from "lucide-react";

/* LaneMemoryCard
 * ----------------
 * A soft, transparent peek at the story Lane has been building about the
 * caregiver. Builds trust ("Lane really does know me") and gives the
 * caregiver agency — if anything is wrong, they tap "Edit my story".
 *
 * Renders nothing until there's at least one fact worth showing — we don't
 * want a hollow card on day 1 before the caregiver has told Lane anything.
 */
export default function LaneMemoryCard({ sid }) {
    const [mem, setMem] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const r = await api.get(`/lane/memory/${sid}`);
                if (active) setMem(r.data);
            } catch {
                /* silent — this card is best-effort */
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [sid]);

    if (loading || !mem || mem.empty) return null;

    const lovedName = mem.loved_one_name;
    const cgName = mem.caregiver_name;
    // Compose the title — gentle, in plain language.
    const title = lovedName
        ? `What Lane remembers about ${lovedName}`
        : "What Lane remembers about you";

    return (
        <section data-testid="lane-memory" className="ml-card p-5 md:p-6 bg-teal-soft !border-teal-soft animate-fade-in-up">
            <div className="flex items-start gap-3">
                <div className="shrink-0 w-11 h-11 rounded-2xl bg-white flex items-center justify-center">
                    <Brain strokeWidth={1.6} size={20} className="text-teal-dark" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <p className="text-sm uppercase tracking-wider font-semibold text-[#6E7A74]">
                                Lane&apos;s notes
                            </p>
                            <h3 className="mt-0.5 text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                                {title}
                            </h3>
                        </div>
                    </div>

                    {/* The headline — last visit + what's happened since */}
                    {(mem.last_visit || mem.since_summary) && (
                        <p className="mt-3 text-[#1F2624] leading-relaxed">
                            {mem.last_visit && (
                                <>We last talked {mem.last_visit}. </>
                            )}
                            {mem.since_summary}
                        </p>
                    )}

                    {/* The facts grid — who they are. Single column keeps
                        long values (medical notes, biggest fear) from
                        being crushed into a 3-word-wide column when the
                        card sits in the dashboard's right rail. */}
                    {mem.facts?.length > 0 && (
                        <dl className="mt-4 grid grid-cols-1 gap-y-2.5 text-sm">
                            {mem.stage && (
                                <div>
                                    <dt className="text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">Stage</dt>
                                    <dd className="text-[#1F2624] font-medium leading-snug">{mem.stage}</dd>
                                </div>
                            )}
                            {mem.facts.map(f => (
                                <div key={f.label}>
                                    <dt className="text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">{f.label}</dt>
                                    <dd className="text-[#1F2624] leading-snug break-words">{f.value}</dd>
                                </div>
                            ))}
                        </dl>
                    )}

                    {/* Wellness flags — what's elevated */}
                    {mem.wellness_flags?.length > 0 && (
                        <p className="mt-3 text-sm text-coral-dark">
                            <Sparkles size={12} className="inline mr-1" />
                            Lane sees that {mem.wellness_flags.join(", ")} — we'll be gentle with you today.
                        </p>
                    )}

                    {/* Recurring themes */}
                    {mem.themes?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="lane-memory-themes">
                            <span className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mr-1 self-center">
                                On your mind lately:
                            </span>
                            {mem.themes.map(t => (
                                <span key={t} className="px-2.5 py-0.5 rounded-2xl bg-white border border-cream-dark text-xs text-[#1F2624]">
                                    {t}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Worked-before tips */}
                    {mem.worked_before?.length > 0 && (
                        <div className="mt-3" data-testid="lane-memory-worked">
                            <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74]">
                                What's worked before
                            </p>
                            <ul className="mt-1 space-y-1 text-sm text-[#1F2624]">
                                {mem.worked_before.map((w, i) => (
                                    <li key={i}>
                                        <span className="text-[#4C5551]">For {w.behavior}:</span> {w.helped}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* CTA */}
                    <Link
                        to="/chat"
                        data-testid="lane-memory-chat"
                        className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-teal-dark hover:text-teal"
                    >
                        Talk to Lane — pick up where we left off
                        <ArrowRight size={14} strokeWidth={1.8} />
                    </Link>
                </div>
            </div>
        </section>
    );
}
